import { Menu, Transition } from '@headlessui/react'
import { Fragment, useEffect, useRef, useState } from 'react'

import ArrowUpRight from '@Icons/arrow-narrow-up-right.svg?react'
import DeleteFilterIcon from '@Icons/delete-filter-icon.svg?react'
import EditFilterIcon from '@Icons/filter-save-icon-blue.svg?react'
import EditFilterGrayIcon from '@Icons/filter-save-icon-gray.svg?react'
import XClose from '@Icons/x-close-filter.svg?react'
import { userIsChannelBasedSelector } from '@Store/api/auth'
import {
  useAddQuickFilterMutation,
  useDeleteFilterMutation,
  useStartExportMutation,
  useUpdateQuickFilterMutation,
} from '@Store/api/product'
import { useAppDispatch, useAppSelector } from '@Store/hooks'
import { setExportStatus } from '@Store/slices/settingsSlice'
import type { ProductsFilterType } from '@Store/types'

import ExportSpinner from '../../../../../assets/Loaders/spin/export-spinner.svg?react'
import FilterDeleteConfirmModal from '../filters-delete-confirm-modal'
import FilterSaveChoiceModal from '../filters-save-choice-modal'
import FilterSaveModal from '../filters-save-modal'
import { COMPETITOR_ATTRIBUTE_CONFIG, PRODUCT_ATTRIBUTE_CONFIG } from './config'
import ConditionRow from './row'
import { buildFilterUrl, fromAPIParams, toAPIParams } from './serializer'
import type {
  ConditionRow as ConditionRowType,
  FilterBuilderState,
} from './types'

type Props = {
  initialFilters: ProductsFilterType
  filterName?: string
  filterId?: number | string
  onClose: () => void
  onApply: (filters: ProductsFilterType) => void
  onReset: (refetch?: boolean) => void
  onSaveFilter: (values: any, url?: string) => void
  setPanelVisible: (visible: boolean | null) => void
}

let idCounter = 0
const makeId = () => `row-${Date.now()}-${++idCounter}`

function OperatorToggle({
  value,
  onChange,
}: {
  value: 'and' | 'or'
  onChange: (v: 'and' | 'or') => void
}) {
  return (
    <div className="flex rounded overflow-hidden border border-gray-200 text-xs font-semibold">
      <button
        type="button"
        onClick={() => onChange('and')}
        className={`px-2 py-0.5 transition-colors ${
          value === 'and'
            ? 'bg-primary-600 text-white'
            : 'bg-white text-gray-500 hover:bg-gray-50'
        }`}
      >
        AND
      </button>
      <button
        type="button"
        onClick={() => onChange('or')}
        className={`px-2 py-0.5 transition-colors border-l border-gray-200 ${
          value === 'or'
            ? 'bg-primary-600 text-white'
            : 'bg-white text-gray-500 hover:bg-gray-50'
        }`}
      >
        OR
      </button>
    </div>
  )
}

const DEFAULT_PRODUCT_ATTR = 'name'
const DEFAULT_COMPETITOR_ATTR = 'competitor'

export default function FilterConditionBuilder({
  initialFilters,
  filterName,
  filterId,
  onClose,
  onApply,
  onReset,
  onSaveFilter,
  setPanelVisible,
}: Props) {
  const initialState = fromAPIParams(initialFilters)
  const [productRows, setProductRows] = useState<ConditionRowType[]>(
    initialState.productRows,
  )
  const [competitorRows, setCompetitorRows] = useState<ConditionRowType[]>(
    initialState.competitorRows,
  )
  const [productOperator, setProductOperator] = useState(
    initialState.productOperator,
  )
  const [competitorOperator, setCompetitorOperator] = useState(
    initialState.competitorOperator,
  )
  const [sectionOperator, setSectionOperator] = useState(
    initialState.sectionOperator,
  )
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [saveChoiceModalOpen, setSaveChoiceModalOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const savedBaseline = useRef(initialFilters)

  // initialFilters can change from outside the panel (e.g. a dashboard
  // shortcut navigating with new query params) while this component stays
  // mounted, so re-sync the rows whenever the incoming filters actually change.
  const initialFiltersKey = JSON.stringify(initialFilters)
  const lastSyncedFiltersKey = useRef(initialFiltersKey)
  useEffect(() => {
    if (initialFiltersKey === lastSyncedFiltersKey.current) return
    lastSyncedFiltersKey.current = initialFiltersKey
    const nextState = fromAPIParams(initialFilters)
    setProductRows(nextState.productRows)
    setCompetitorRows(nextState.competitorRows)
    setProductOperator(nextState.productOperator)
    setCompetitorOperator(nextState.competitorOperator)
    setSectionOperator(nextState.sectionOperator)
    savedBaseline.current = initialFilters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFiltersKey])

  const dispatch = useAppDispatch()
  const canExport = useAppSelector((state) => !state.settings.is_export_ongoing)
  const isChannelPackage = useAppSelector(userIsChannelBasedSelector)

  const [addQuickFilter, { isLoading: isAdding }] = useAddQuickFilterMutation()
  const [updateQuickFilter, { isLoading: isUpdating }] =
    useUpdateQuickFilterMutation()
  const [deleteFilter] = useDeleteFilterMutation()
  const [startExport, { isLoading: isExportLoading }] = useStartExportMutation()

  const currentState: FilterBuilderState = {
    productRows,
    competitorRows,
    productOperator,
    competitorOperator,
    sectionOperator,
  }

  const hasModified = (() => {
    if (!filterId) return false
    return (
      buildFilterUrl(currentState) !==
      buildFilterUrl(fromAPIParams(savedBaseline.current))
    )
  })()

  // Condition helpers
  const addProductRow = () =>
    setProductRows((prev) => [
      ...prev,
      {
        id: makeId(),
        attribute: DEFAULT_PRODUCT_ATTR,
        value: '',
        operator: productOperator,
        filterOperator:
          PRODUCT_ATTRIBUTE_CONFIG[DEFAULT_PRODUCT_ATTR]?.operatorOptions?.[0]
            ?.value,
      },
    ])

  const addCompetitorRow = () =>
    setCompetitorRows((prev) => [
      ...prev,
      {
        id: makeId(),
        attribute: DEFAULT_COMPETITOR_ATTR,
        value: '',
        operator: competitorOperator,
        filterOperator:
          COMPETITOR_ATTRIBUTE_CONFIG[DEFAULT_COMPETITOR_ATTR]
            ?.operatorOptions?.[0]?.value,
      },
    ])

  const updateProductRow = (id: string, updates: Partial<ConditionRowType>) =>
    setProductRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...updates } : r)),
    )

  const updateCompetitorRow = (
    id: string,
    updates: Partial<ConditionRowType>,
  ) =>
    setCompetitorRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...updates } : r)),
    )

  const handleProductOperatorChange = (op: 'and' | 'or') => {
    setProductOperator(op)
    setProductRows((prev) => prev.map((r) => ({ ...r, operator: op })))
  }

  const handleCompetitorOperatorChange = (op: 'and' | 'or') => {
    setCompetitorOperator(op)
    setCompetitorRows((prev) => prev.map((r) => ({ ...r, operator: op })))
  }

  const updateProductRowOperator = (id: string, op: 'and' | 'or') =>
    setProductRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, operator: op } : r)),
    )

  const updateCompetitorRowOperator = (id: string, op: 'and' | 'or') =>
    setCompetitorRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, operator: op } : r)),
    )

  const removeProductRow = (id: string) =>
    setProductRows((prev) => prev.filter((r) => r.id !== id))

  const removeCompetitorRow = (id: string) =>
    setCompetitorRows((prev) => prev.filter((r) => r.id !== id))

  // Actions
  const handleApply = () => {
    const params = toAPIParams(currentState)
    onApply(params)
  }

  const handleReset = () => {
    setProductRows([])
    setCompetitorRows([])
    onReset(true)
    setPanelVisible(false)
  }

  const handleSaveNew = async (name: string) => {
    const url = buildFilterUrl(currentState)
    try {
      await addQuickFilter({ name, url }).unwrap()
      onSaveFilter(toAPIParams(currentState), url)
      handleApply()
      setSaveModalOpen(false)
    } catch {}
  }

  const handleUpdate = async () => {
    if (!filterId) return
    const url = buildFilterUrl(currentState)
    try {
      await updateQuickFilter({ id: Number(filterId), url }).unwrap()
      savedBaseline.current = toAPIParams(currentState)
      onSaveFilter(toAPIParams(currentState), url)
      handleApply()
    } catch {}
  }

  const handleDelete = async () => {
    if (!filterId) return
    await deleteFilter(filterId)
    onReset(true)
    setPanelVisible(false)
  }

  const exportOptions = [
    { text: '.zip Horizontal', value: 'zip_horizontal' },
    { text: '.xlsx Horizontal', value: 'xlsx_horizontal' },
    { text: '.csv Horizontal', value: 'csv_horizontal' },
    { text: '.zip Vertical', value: 'zip_vertical' },
    { text: '.xlsx Vertical', value: 'xlsx_vertical' },
    { text: '.csv Vertical', value: 'csv_vertical' },
  ]

  const handleExport = async (exportType: string) => {
    if (isExportLoading) return
    try {
      const conditions = toAPIParams(currentState) as any
      await startExport(
        Object.keys(conditions).length > 0
          ? { export_type: exportType, conditions }
          : { export_type: exportType },
      ).unwrap()
      dispatch(setExportStatus({ is_export_ongoing: true }))
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <>
      <FilterDeleteConfirmModal
        isOpen={deleteConfirmOpen}
        setIsOpen={setDeleteConfirmOpen}
        filterName={filterName}
        onConfirm={handleDelete}
      />
      <FilterSaveChoiceModal
        isOpen={saveChoiceModalOpen}
        setIsOpen={setSaveChoiceModalOpen}
        filterName={filterName}
        hasModified={hasModified}
        isLoading={isUpdating}
        onUpdate={handleUpdate}
        onSaveNew={() => setSaveModalOpen(true)}
      />
      <FilterSaveModal
        isOpen={saveModalOpen}
        setIsOpen={setSaveModalOpen}
        isLoading={isAdding}
        onConfirm={(name) => async () => handleSaveNew(name)}
      />

      <div className="border border-gray-200 rounded-lg bg-white mb-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-base text-gray-900">
            {filterName ?? 'New Filter'}
            {filterId && (
              <span className="ml-1.5 font-normal text-sm text-gray-400">
                (Collection)
              </span>
            )}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XClose className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-3">
          {/* PRODUCT section */}
          <div className="border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between px-4 pt-3 pb-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Product
                </span>
              </div>
              {/* AND/OR toggle hidden until OR is implemented in backend */}
              {/* {productRows.length > 1 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400">Match all</span>
                  <OperatorToggle
                    value={productOperator}
                    onChange={handleProductOperatorChange}
                  />
                </div>
              )} */}
            </div>
            <div className="px-4 pb-3">
              {productRows.length === 0 ? (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-400">
                    No conditions yet
                  </span>
                  <button
                    type="button"
                    onClick={addProductRow}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                  >
                    + Add filter
                  </button>
                </div>
              ) : (
                <>
                  {productRows.map((row, i) => (
                    <ConditionRow
                      key={row.id}
                      row={row}
                      index={i}
                      sectionType="product"
                      usedAttributes={productRows.map((r) => r.attribute)}
                      isChannelPackage={isChannelPackage}
                      onUpdate={updateProductRow}
                      onOperatorChange={updateProductRowOperator}
                      onRemove={removeProductRow}
                      onSubmit={handleApply}
                    />
                  ))}
                  {productRows.length <
                    Object.keys(PRODUCT_ATTRIBUTE_CONFIG).length && (
                    <button
                      type="button"
                      onClick={addProductRow}
                      className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      + Add product attribute
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Separator */}
          {/* AND/OR section toggle hidden until OR is implemented in backend */}
          {/* <div className="flex items-center gap-3 py-1">
            <div className="flex-1 border-t border-gray-200" />
            <div className="flex items-center gap-2">
              <OperatorToggle
                value={sectionOperator}
                onChange={setSectionOperator}
              />
              <span className="text-xs text-gray-300">
                Product → Competitor
              </span>
            </div>
            <div className="flex-1 border-t border-gray-200" />
          </div> */}

          {/* COMPETITOR section */}
          <div className="border border-gray-200 rounded-lg bg-green-50/30">
            <div className="flex items-center justify-between px-4 pt-3 pb-2">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Competitor
                </span>
              </div>
              {/* AND/OR toggle hidden until OR is implemented in backend */}
              {/* {competitorRows.length > 1 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400">Match all</span>
                  <OperatorToggle
                    value={competitorOperator}
                    onChange={handleCompetitorOperatorChange}
                  />
                </div>
              )} */}
            </div>
            <div className="px-4 pb-3">
              {competitorRows.length === 0 ? (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-400">
                    No conditions yet
                  </span>
                  <button
                    type="button"
                    onClick={addCompetitorRow}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                  >
                    + Add filter
                  </button>
                </div>
              ) : (
                <>
                  {competitorRows.map((row, i) => (
                    <ConditionRow
                      key={row.id}
                      row={row}
                      index={i}
                      sectionType="competitor"
                      usedAttributes={competitorRows.map((r) => r.attribute)}
                      isChannelPackage={isChannelPackage}
                      onUpdate={updateCompetitorRow}
                      onOperatorChange={updateCompetitorRowOperator}
                      onRemove={removeCompetitorRow}
                      onSubmit={handleApply}
                    />
                  ))}
                  {competitorRows.length <
                    Object.keys(COMPETITOR_ATTRIBUTE_CONFIG).length && (
                    <button
                      type="button"
                      onClick={addCompetitorRow}
                      className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      + Add competitor attribute
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-y-2 px-5 py-3 border-t border-gray-100">
          {/* Left: save + reset + delete */}
          <div className="flex items-center gap-3">
            {filterId ? (
              <>
                <button
                  type="button"
                  onClick={() => setSaveChoiceModalOpen(true)}
                  className="flex items-center gap-1.5 text-sm"
                >
                  <EditFilterIcon className="w-4 h-4" />
                  <span className="text-primary-600 font-medium">
                    Save filter
                  </span>
                </button>
                <span className="text-gray-300 select-none">·</span>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmOpen(true)}
                  className="flex items-center gap-1.5 text-sm text-error-600 hover:text-error-700"
                >
                  <DeleteFilterIcon className="w-4 h-4" />
                  Delete filter
                </button>
                <span className="text-gray-300 select-none">·</span>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setSaveModalOpen(true)}
                  className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-800"
                >
                  <EditFilterGrayIcon className="w-4 h-4" />
                  Save Filter (Collection)
                </button>
                <span className="text-gray-300 select-none">·</span>
              </>
            )}
            <button
              type="button"
              onClick={handleReset}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Reset filter
            </button>
          </div>

          {/* Right: export + apply */}
          <div className="flex items-center gap-2">
            <Menu as="div" className="relative">
              <Menu.Button
                disabled={!canExport}
                className="flex items-center gap-1.5 text-sm text-gray-700 font-medium border border-gray-300 rounded-lg px-4 py-2 hover:bg-gray-50 disabled:opacity-40"
              >
                {isExportLoading ? (
                  <ExportSpinner className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowUpRight className="w-4 h-4" />
                )}
                Export Results
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 bottom-full mb-1 w-48 origin-bottom-right rounded-lg bg-white shadow-lg border border-gray-100 z-50">
                  <div className="py-1">
                    {exportOptions.map((opt) => (
                      <Menu.Item key={opt.value}>
                        {({ active }) => (
                          <button
                            onClick={() => handleExport(opt.value)}
                            className={`w-full text-left px-3 py-2 text-sm text-gray-700 ${active ? 'bg-gray-50' : ''}`}
                          >
                            {opt.text}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            <button
              type="button"
              onClick={handleApply}
              className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-5 py-2 rounded-lg"
            >
              Filter
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
